import { DomUtil, StringUtil } from "./utils";
import ModalsContainer from "./components/modal/modals-container";

// Inspiration from Vue.js modal plugin https://github.com/euvl/vue-js-modal
const ModalCore = (Vue, options = {}) => {
  // Global modal event subscription/event bus
  const subscription = new Vue();

  const config = {
    implementerName: getImplementerName(options.implementerName)
  };

  /**
   * Contains current context variables like root = parent = Caller Vue instance
   */
  const context = {
    root: null
  };

  /**
   * Creates a container for modals in the root Vue component.
   */
  const setDynamicModalContainer = parent => {
    // The parent is the implementer Vue instance
    context.root = parent;

    // Create new div in current body
    const element = DomUtil.createDivInBody();

    // Mount a new vue instance on the newly created div
    new Vue({
      parent,
      render: h => h(ModalsContainer)
    }).$mount(element);
  };

  /**
   * Show a static modal defined in own component like:
   * <modal name="a-static-modal">
   *   This is my static modal
   * </modal>
   * ...
   * this.$bvmModal.show("a-static-modal");
   */
  const showStaticModal = (name, params) => {
    subscription.$emit("toggle", name, true, params);
  };

  /**
   * /**
   * Show a dynamic modal called from code:
   * this.$bvmModal.show(
   *   component,
   *   component_properties,
   *   modal_properties,
   *   modal_events
   * );
   *
   * @param component Modal component to show (advised to use a pre-styled base modal component. See modal/base directory)
   * @param componentProps Props for given component
   * @param modalProps Props for the modal component. See modal.vue
   * @param modalEvents Events from the modal component. See modal.vue
   */
  const showDynamicModal = (
    component,
    componentProps,
    modalProps = {},
    modalEvents
  ) => {
    // Get root modals-container in current Vue instance
    const container = (context.root || {}).__bvmModalContainer;
    // Optional initial defaults
    const defaults = options.dynamicDefaults || {};

    if (!container) {
      return;
    }

    container.add(
      component,
      componentProps,
      { ...defaults, ...modalProps },
      modalEvents
    );
  };

  /**
   * Show modal with given properties.
   * The first Property change:
   */
  const show = (...args) => {
    const [modal] = args;

    switch (typeof modal) {
      case "string":
        showStaticModal(...args);
        break;

      case "object":
      case "function":
        showDynamicModal(...args);
        break;

      default:
        console.warn(
          "Received an unsupported argument type as a first argument: ",
          modal
        );
    }
  };

  const hide = (name, params) => {
    subscription.$emit("toggle", name, false, params);
  };

  const hideAll = () => {
    subscription.$emit("hide-all");
  };

  const toggle = (name, params) => {
    subscription.$emit("toggle", name, undefined, params);
  };

  return {
    config,
    context,
    subscription,
    setDynamicModalContainer,
    show,
    hide,
    hideAll,
    toggle
  };
};

function getImplementerName(implementerName) {
  if (!implementerName) {
    implementerName = StringUtil.randomString(4);
    console.log(
      `No implementerName option given, setting random unique name: ${implementerName}`
    );
  }

  return implementerName;
}

export default ModalCore;
